import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { IconMail, IconX } from '@tabler/icons-react'
import { useI18n } from '../../../lib/i18n'

export function BookingTeamEmailButton({ name, email, busy, onSave }: {
  name: string
  email: string
  busy: boolean
  onSave: (email: string) => Promise<boolean>
}) {
  const { t } = useI18n()
  const id = useId()
  const trigger = useRef<HTMLButtonElement>(null)
  const popup = useRef<HTMLFormElement>(null)
  const [position, setPosition] = useState<{ left: number; top: number; above: boolean } | null>(null)
  const [draft, setDraft] = useState(email)
  const [error, setError] = useState('')
  const open = position !== null
  const place = () => {
    const rect = trigger.current?.getBoundingClientRect()
    if (!rect) return
    const above = window.innerHeight - rect.bottom < 200 && rect.top > 200
    setPosition({ left: Math.max(12, Math.min(rect.right - 300, window.innerWidth - 312)), top: above ? rect.top - 8 : rect.bottom + 8, above })
  }
  const close = () => { setPosition(null); trigger.current?.focus() }
  useEffect(() => {
    if (!open) return
    const outside = (event: PointerEvent) => {
      const target = event.target as Node
      if (!trigger.current?.contains(target) && !popup.current?.contains(target)) setPosition(null)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      event.preventDefault()
      close()
    }
    document.addEventListener('pointerdown', outside)
    document.addEventListener('keydown', escape, true)
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      document.removeEventListener('pointerdown', outside)
      document.removeEventListener('keydown', escape, true)
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open])
  const save = async () => {
    const value = draft.trim().toLowerCase()
    if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) { setError(t('bookings.team.emailInvalid')); return }
    if (value === email || await onSave(value)) close()
  }
  return <>
    <button ref={trigger} type="button" className={`dash-team-email-btn${email ? ' has-email' : ''}`}
      aria-label={`${t('bookings.team.email')}: ${name}`} title={email || t('bookings.team.addEmail')}
      aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? id : undefined} disabled={busy}
      onClick={() => { if (open) { close(); return }; setDraft(email); setError(''); place() }}>
      <IconMail size={15} aria-hidden="true" />
    </button>
    {position ? createPortal(
      <form ref={popup} id={id} role="dialog" aria-modal="false" aria-labelledby={`${id}-label`}
        className="dash-team-email-popup" style={{ left: position.left, top: position.top, transform: position.above ? 'translateY(-100%)' : undefined }}
        onSubmit={(event) => { event.preventDefault(); void save() }}>
        <header><strong id={`${id}-label`}>{t('bookings.team.email')} · {name}</strong>
          <button type="button" className="dash-team-remove" aria-label={t('bookings.new.close')} disabled={busy} onClick={close}><IconX size={14} /></button>
        </header>
        <input autoFocus type="email" aria-label={t('bookings.team.email')} value={draft} maxLength={254}
          placeholder={t('offers.notify.personEmail')} disabled={busy} aria-invalid={!!error}
          onChange={(event) => { setDraft(event.target.value); setError('') }} />
        <small>{t('bookings.team.addEmail')}</small>
        {error ? <p className="dash-team-error" role="alert">{error}</p> : null}
        <button type="submit" className="dash-primary-btn" disabled={busy}>{t(busy ? 'common.saving' : 'common.save')}</button>
      </form>, document.body,
    ) : null}
  </>
}
